import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { Box, Link } from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { useThemeMode } from 'src/theme'; // Import your custom real-time theme hook

// ----------------------------------------------------------------------

const Logo = forwardRef(
  ({ disabledLink = false, isExpanded = true, sx, ...other }, ref) => {
    const { isSmallScreen } = useResponsiveScreen();
    const { themeMode } = useThemeMode(); // Access the active state directly in real-time

    const isDark = themeMode === 'dark';

    const logo = (
      <Box
        ref={ref}
        component="div"
        sx={{
          width: isExpanded ? (isSmallScreen ? '20vh' : '40vh') : '64px',
          height: isExpanded ? (isSmallScreen ? '13vh' : '20vh') : '64px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'width',
          ...sx,
        }}
        {...other}
      >
        {/* 1. EXPANDED LOGO: Swaps automatically based on themeMode */}
        <Box
          component="img"
          src={
            isDark
              ? '/assets/images/Oryxonelogodark.png' // Dark-mode logo (white lines)
              : '/assets/images/OryXonelogo.png' // Light-mode logo (dark lines)
          }
          alt="Oryx One Logo"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            position: 'absolute',
            opacity: isExpanded ? 1 : 0,
            transform: isExpanded ? 'scale(1)' : 'scale(0.8)',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'opacity, transform',
          }}
        />

        {/* 2. REDUCED LOGO: Swaps automatically based on themeMode */}
        <Box
          component="img"
          src={
            isDark
              ? '/favicon/apple-touch-icon_dark.png' // Dark-mode collapsed logo
              : '/assets/images/smoloryxone.png' // Light-mode collapsed logo
          }
          alt="logo"
          sx={{
            width: '32px',
            height: '32px',
            objectFit: 'contain',
            position: 'absolute',
            opacity: isExpanded ? 0 : 1,
            transform: isExpanded ? 'scale(0.5) rotate(-45deg)' : 'scale(1) rotate(0deg)',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'opacity, transform',
          }}
        />
      </Box>
    );

    if (disabledLink) {
      return <>{logo}</>;
    }

    return (
      <Link to="/" component={RouterLink} sx={{ display: 'contents' }}>
        {logo}
      </Link>
    );
  }
);

Logo.propTypes = {
  sx: PropTypes.object,
  disabledLink: PropTypes.bool,
  isExpanded: PropTypes.bool,
};

export default Logo;
